import { ReactNode } from 'react';

/**
 * Enum representing all supported modal layout variants.
 * Each variant adjusts layout, height, scroll behavior, and styling.
 */
export enum EModalVariants {
  /** Standard modal with rounded corners and medium height */
  DEFAULT = 'default',
  /** Compact modal optimized for location-based forms or actions */
  LOCATION = 'location',
  /** Full-height, scrollable modal for lead generation forms */
  LEAD_GEN = 'leadGen',
  /** Full-viewport modal designed for slideshows or gallery content */
  SLIDESHOW = 'slideshow',
  /** Small, focused modal optimized for login forms */
  LOG_IN = 'login',
}

/**
 * Props for the MlModal component.
 * These props control layout, content, lifecycle callbacks, and styling.
 */
export interface IMlModalProps {
  /**
   * Content to render inside the modal.
   */
  children: ReactNode;
  /**
   * Optional flag to allow closing the modal when clicking the overlay.
   * Currently not implemented in the base modal logic.
   */
  closeOnOverlayClick?: boolean;
  /**
   * Optional callback fired when the modal opens.
   */
  onOpen?: () => void;
  /**
   * Optional callback fired when the modal closes.
   */
  onClose?: () => void;
  /**
   * Optional class name to apply to the modal wrapper for custom styling.
   */
  className?: string;
  /**
   * Modal layout variant. Controls visual layout and max height/width.
   */
  variant?: EModalVariants;
}

/**
 * Ref object returned from MlModal via `useImperativeHandle`.
 * Allows external control over modal open/close behavior.
 */
export interface IModalRefProps {
  /**
   * Programmatically opens the modal.
   */
  handleOpen: () => void;
  /**
   * Programmatically closes the modal.
   */
  handleClose: () => void;
  /**
   * Ref to the underlying <dialog> element.
   */
  dialogRef: HTMLDialogElement | null;
  isOpen: boolean;
}
